import { useState } from "react";
import { useExpandedId } from "@common/hooks/useExpandedId";
import { useTranslation } from "@localization/useTranslation";
import { LinkedEntityCollapseListProps } from "./LinkedEntityCollapseList";

// A card is identified by its position, the way a supplier card is identified by its own row: picking another item
// on a card changes neither its key nor which card is open, so the card stays put while the change is saved.
export function useLinkedEntityCollapseList(props: LinkedEntityCollapseListProps) {
    const { translateString } = useTranslation();
    const { getExpandedId, setExpandedId } = useExpandedId();
    // The open card lives here because the router delivers the hash after the saved list, which would leave renders
    // where the hash and the cards disagree. The hash still records the open item, so Back reopens it.
    const [expandedIndex, setExpandedIndex] = useState<number | undefined>(() => getIndexOf(getExpandedId()));
    const [addingIndex, setAddingIndex] = useState<number | undefined>(undefined);
    const [pickedId, setPickedId] = useState<string | undefined>(undefined);

    // A template preview renders the field without an entity, so there is nothing to load or change.
    const isDisabled = props.disabled || props.isPreview;
    const isLoading = props.isLoading && !props.isPreview;
    // The adding card stays until the saved list holds the picked item at its position, and is then replaced by
    // that item's card without a render where neither is shown.
    const isAdding = addingIndex !== undefined && addingIndex === props.selectedIds.length;

    function getIndexOf(id: string | undefined) {
        const index = id ? props.selectedIds.indexOf(id) : -1;
        return index === -1 ? undefined : index;
    }

    function getName(id: string) {
        return props.selectableItems?.find((item) => item.id === id)?.name ?? "";
    }

    function getLink(id: string) {
        const href = props.getLinkTo(id);
        return href ? { href, hoverText: translateString("goToWithName", { name: getName(id) }) } : undefined;
    }

    // A card may keep its own item, but never pick one another card already holds.
    function getSelectableItems(currentId?: string) {
        return props.selectableItems?.filter((item) => item.id === currentId || !props.selectedIds.includes(item.id));
    }

    function openCard(index: number | undefined, id: string) {
        setExpandedIndex(index);
        setExpandedId(id);
    }

    function onExpandClick(index: number) {
        if (expandedIndex === index) {
            openCard(undefined, "");
        } else {
            openCard(index, props.selectedIds[index]);
        }
    }

    function startAdding() {
        setAddingIndex(props.selectedIds.length);
        setPickedId(undefined);
        openCard(props.selectedIds.length, "");
    }

    // Also run after a removal: the position of a finished add is kept, and a shorter list would bring its card back.
    function cancelAdding() {
        setAddingIndex(undefined);
        openCard(undefined, "");
    }

    function addItem(id: string) {
        setPickedId(id);
        setExpandedId(id);
        props.onChange([...props.selectedIds, id]);
    }

    function swapItem(index: number, newId: string) {
        setExpandedId(newId);
        props.onChange(props.selectedIds.map((id, selectedIndex) => (selectedIndex === index ? newId : id)));
    }

    // Only the open card has a remove button, so removing always closes it.
    function removeItem(index: number) {
        cancelAdding();
        props.onChange(props.selectedIds.filter((_, selectedIndex) => selectedIndex !== index));
    }

    return {
        isDisabled,
        isLoading,
        isAdding,
        pickedId,
        expandedIndex,
        getName,
        getLink,
        getSelectableItems,
        onExpandClick,
        startAdding,
        cancelAdding,
        addItem,
        swapItem,
        removeItem,
    };
}
